import React, { createContext, useContext, useState, useEffect } from 'react'
import api from '../services/api'
import { useAuth } from './AuthContext'

const BalanceContext = createContext()

export function useBalance() {
  const context = useContext(BalanceContext)
  if (!context) {
    throw new Error('useBalance must be used within a BalanceProvider')
  }
  return context
}

export function BalanceProvider({ children }) {
  const { user } = useAuth()
  const [categories, setCategories] = useState([])
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Calculate totals and derived state
  const totalSpent = transactions
    .filter(t => t.status === 'success')
    .reduce((sum, t) => sum + t.amount, 0)

  const totalBudget = user?.salary || 0

  // Derive categories with real-time balance calculation
  // This ensures balance is always consistent with transaction history
  const derivedCategories = categories.map(cat => {
    const spent = transactions
      .filter(t => t.category_id === cat.id && (t.status === 'success' || t.status === 'pending'))
      .reduce((sum, t) => sum + t.amount, 0)

    let allocated = 0
    if (cat.percentage > 0) {
      allocated = (totalBudget * (cat.percentage || 0)) / 100
    } else {
      allocated = cat.fixed_amount || 0
    }

    return {
      ...cat,
      // If we have local override (optimistic), use it? 
      // Actually, deriving from transactions is safer and handles optimistic transactions too if added to list
      current_balance: allocated - spent,
      allocated,
      spent
    }
  })

  const totalBalance = derivedCategories.reduce((sum, cat) => sum + (cat.current_balance || 0), 0)

  // Load data from API
  const loadData = async () => {
    if (!user) return

    try {
      setLoading(true)
      setError('')

      const [categoriesRes, transactionsRes] = await Promise.all([
        api.get('/categories'),
        api.get('/transactions')
      ])

      setCategories(categoriesRes.data.categories || [])
      setTransactions(transactionsRes.data.transactions || [])
    } catch (err) {
      console.error('❌ Failed to load balance data:', err)
      setError(err.userMessage || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  // Update category balance (optimistic update)
  const updateCategoryBalance = (categoryId, newBalance) => {
    // With derived state, we don't need to manually update balance if we add the transaction
    // But keeping this for compatibility if explicit update is needed
    setCategories(prev =>
      prev.map(cat =>
        cat.id === categoryId
          ? { ...cat, current_balance: newBalance }
          : cat
      )
    )
  }

  // Add transaction (optimistic update)
  const addTransaction = (transaction) => {
    setTransactions(prev => [transaction, ...prev])
  }

  // Update transaction status
  const updateTransactionStatus = (transactionId, status) => {
    setTransactions(prev =>
      prev.map(t =>
        t.id === transactionId
          ? { ...t, status }
          : t
      )
    )
  }

  // Get category by ID
  const getCategoryById = (categoryId) => {
    return derivedCategories.find(cat => cat.id === categoryId)
  }

  // Get spending by category
  const getSpendingByCategory = (categoryId) => {
    return transactions
      .filter(t => t.category_id === categoryId && t.status === 'success')
      .reduce((sum, t) => sum + t.amount, 0)
  }

  // Validate payment amount
  const validatePayment = (categoryId, amount) => {
    const category = getCategoryById(categoryId)
    if (!category) {
      return { valid: false, error: 'Category not found' }
    }

    if (amount <= 0) {
      return { valid: false, error: 'Enter a valid amount' }
    }

    if (category.current_balance < amount) {
      return {
        valid: false,
        error: `Insufficient balance. Available: ₹${category.current_balance.toLocaleString('en-IN')}`
      }
    }

    return { valid: true }
  }

  // Load data when user changes
  useEffect(() => {
    if (user) {
      loadData()
    } else {
      setCategories([])
      setTransactions([])
    }
  }, [user])

  const value = {
    // Data - use derived categories
    categories: derivedCategories,
    transactions,
    loading,
    error,

    // Computed values
    totalBalance,
    totalSpent,
    totalBudget,

    // Actions
    loadData,
    updateCategoryBalance,
    addTransaction,
    updateTransactionStatus,
    getCategoryById,
    getSpendingByCategory,
    validatePayment,

    // Setters for direct updates
    setCategories,
    setTransactions,
    setError
  }

  return (
    <BalanceContext.Provider value={value}>
      {children}
    </BalanceContext.Provider>
  )
}